import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useMembership } from '../hooks/useMembership';
import advertisementApi from '../services/advertisementApi';

const AdvertisementContext = createContext(null);

export const useAdvertisement = () => {
  const context = useContext(AdvertisementContext);
  if (!context) {
    return null;
  }
  return context;
};

export const AdvertisementProvider = ({ children }) => {
  const { user, authToken, isApiReachable, isAuthReady } = useAuth();
  const { membershipType } = useMembership();
  const [advertisements, setAdvertisements] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timeoutErrorCountRef = useRef({}); // { position: count }

  // Clear ads when going offline
  useEffect(() => {
    if (isApiReachable === false) {
      setAdvertisements({});
    }
  }, [isApiReachable]);

  // Belirli bir pozisyon için reklamları yükle
  const loadAdvertisements = useCallback(
    async (position) => {
      if (!position) return;

      // CRITICAL: Wait for auth to be ready before making requests
      if (!isAuthReady) {
        return; // Wait for auth bootstrap to complete
      }

      // Don't load ads if API is unreachable
      if (isApiReachable === false) {
        setAdvertisements((prev) => ({
          ...prev,
          [position]: [],
        }));
        return;
      }

      // Don't load ads if API reachability is unknown (still checking)
      if (isApiReachable === null) {
        return; // Wait for connectivity check
      }

      try {
        setLoading(true);
        setError(null);

        const result = await advertisementApi.getActiveAdvertisements({
          position,
          membershipType: membershipType || 'free',
        });

        if (result.success && result.advertisements) {
          // Başarılı yükleme sonrası hata sayacını sıfırla
          timeoutErrorCountRef.current[position] = 0;
          setAdvertisements((prev) => ({
            ...prev,
            [position]: result.advertisements,
          }));
        } else {
          // Reklam yoksa boş array set et
          setAdvertisements((prev) => ({
            ...prev,
            [position]: [],
          }));
        }
      } catch (error) {
        // Check if it's an offline error - don't spam logs
        const isOfflineError = 
          error.code === 'NETWORK_UNREACHABLE' ||
          error.isOffline ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
          error.code === 'LOCAL_SERVER_NOT_RUNNING';
        
        if (!isOfflineError) {
          // Only log non-offline errors (and only first few times)
          const errorCount = timeoutErrorCountRef.current[position] || 0;
          timeoutErrorCountRef.current[position] = errorCount + 1;
          if (errorCount < 3) {
            console.warn('Reklamlar yüklenirken hata:', error);
          }
        }
        // Offline errors are handled silently - no log spam
        
        setAdvertisements((prev) => ({
          ...prev,
          [position]: [],
        }));
      } finally {
        setLoading(false);
      }
    },
    [membershipType, isApiReachable, isAuthReady]
  );

  // Reklam gösterimini kaydet
  const recordImpression = useCallback(
    async (adId) => {
      // CRITICAL: Don't make request if auth not ready or no token
      if (!isAuthReady || !authToken) {
        return;
      }
      try {
        await advertisementApi.recordImpression({
          adId,
          token: authToken,
        });
      } catch (error) {
        console.error('Record impression error:', error);
        // Hata olsa bile sessizce devam et
      }
    },
    [authToken, isAuthReady]
  );

  // Reklam tıklamasını kaydet
  const recordClick = useCallback(
    async (adId) => {
      // CRITICAL: Don't make request if auth not ready or no token
      if (!isAuthReady || !authToken) {
        return;
      }
      try {
        await advertisementApi.recordClick({
          adId,
          token: authToken,
        });
      } catch (error) {
        console.error('Record click error:', error);
        // Hata olsa bile sessizce devam et
      }
    },
    [authToken, isAuthReady]
  );

  // Belirli bir pozisyon için reklamları getir
  const getAdvertisements = useCallback(
    (position) => {
      return advertisements[position] || [];
    },
    [advertisements]
  );

  const value = {
    advertisements,
    loading,
    error,
    loadAdvertisements,
    recordImpression,
    recordClick,
    getAdvertisements,
  };

  return <AdvertisementContext.Provider value={value}>{children}</AdvertisementContext.Provider>;
};

export default AdvertisementContext;

